import { Fragment, useState, useRef, useEffect, useCallback } from 'react';
import api from '../services/api';
import { API } from '../App';
import { X, Plus, Trash2, Search, Package, Upload, Loader2, ShoppingCart, History, ChevronDown, CheckCircle2, Mail, FileText, Clock, MessageSquare, PenLine } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { modalV2OverlayClass } from '../utils/modalUiV2';
import { useV3Window } from '../v3/useV3Window';

const enconOrange = '#ee7203';

// ─── Product Search Dropdown ──────────────────────────────────────────────────
function ProductSearchDropdown({ value, onChange, onSelect, placeholder = 'Search by code or description…' }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const modal = inputRef.current.closest('[data-stock-request-modal]');
      const modalRect = modal ? modal.getBoundingClientRect() : null;
      const maxW = modalRect ? modalRect.right - rect.left - 16 : 700;
      setDropPos({ top: rect.bottom + 4, left: rect.left, width: Math.min(maxW, 700) });
    }
  }, [isOpen]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`${API}/stock-database/search`, {
        params: { q, limit: 80, include_all: true }
      });
      setResults(res.data?.results || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    setIsOpen(true);
    search(val);
  };

  const handleSelect = (product) => {
    onSelect(product);
    setIsOpen(false);
    setResults([]);
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={query}
          onChange={handleChange}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          placeholder={placeholder}
          className="pr-8 text-sm text-gray-900"
        />
        <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      </div>

      {isOpen && query.length >= 2 && (
        <div
          className="fixed z-[9999] rounded-xl shadow-2xl overflow-hidden"
          style={{
            background: 'rgba(28,28,28,0.97)',
            border: `2px solid rgba(238,114,3,0.6)`,
            backdropFilter: 'blur(10px)',
            width: `${dropPos.width}px`,
            maxHeight: '280px',
            top: `${dropPos.top}px`,
            left: `${dropPos.left}px`,
          }}
        >
          <div className="sticky top-0 px-4 py-2 text-xs text-gray-400 border-b border-white/10"
            style={{ background: 'rgba(28,28,28,0.97)' }}>
            {loading ? 'Searching…' : `${results.length} results`}
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '230px' }}>
            {loading ? (
              <div className="p-4 text-center text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">No products found</div>
            ) : (
              <div className="divide-y divide-white/10">
                {results.map((p, i) => (
                  <div
                    key={i}
                    className="px-4 py-2.5 cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => handleSelect(p)}
                  >
                    <div className="flex items-center gap-3">
                      <Package className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} />
                      <span className="font-mono font-bold text-sm text-white w-28 flex-shrink-0">
                        {p.product_code || p.code}
                      </span>
                      <span className="text-sm text-gray-300 flex-1 truncate">
                        {p.description || p.name}
                      </span>
                      {(p.on_hand_quantity || 0) > 0 && (
                        <span className="text-xs text-green-400 flex-shrink-0">
                          {p.on_hand_quantity} in stock
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Customer Lines Modal (sub-modal over stock request) ─────────────────────
function CustomerLinesModal({ email, onAdd, onClose, alreadyAdded }) {
  const [selected, setSelected] = useState(() =>
    new Set((email.product_lines || []).map((_, i) => i))
  );

  const lines = email.product_lines || [];
  const allSelected = selected.size === lines.length;

  const toggle = (i) => setSelected(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(lines.map((_, i) => i)));

  const typeStyle = email.email_type === 'order'
    ? { bg: 'bg-orange-100', text: 'text-orange-700', label: 'SA' }
    : email.email_type === 'quote'
    ? { bg: 'bg-purple-100', text: 'text-purple-700', label: 'QT' }
    : { bg: 'bg-gray-100', text: 'text-gray-600', label: '?' };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 10000, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ width: 'min(96vw, 620px)', maxHeight: '80vh', border: `2px solid ${enconOrange}` }}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg ${typeStyle.bg} ${typeStyle.text}`}>
                  {typeStyle.label}
                </span>
                {alreadyAdded && (
                  <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Lines already added
                  </span>
                )}
              </div>
              <h3 className="font-bold text-gray-900 text-base truncate">
                {email.customer_name || email.from_email || 'Unknown customer'}
              </h3>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {email.order_number && (
                  <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded-full">
                    {email.order_number}
                  </span>
                )}
                {email.reference && (
                  <span className="text-xs text-gray-500">Ref: {email.reference}</span>
                )}
                <span className="text-xs text-gray-400">{email.filename}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Lines list */}
        {lines.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-10 text-gray-400">
            <Package className="w-8 h-8 mb-2 text-gray-300" />
            <p className="text-sm">No product lines found in this email</p>
            <p className="text-xs mt-1">The PDF may not have been fully parsed yet</p>
          </div>
        ) : (
          <>
            {/* Select all bar */}
            <div
              className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-3 flex-shrink-0 cursor-pointer hover:bg-gray-50"
              onClick={toggleAll}
            >
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                allSelected ? 'border-orange-500 bg-orange-500' : selected.size > 0 ? 'border-orange-400 bg-orange-100' : 'border-gray-300'
              }`}>
                {allSelected && <span className="text-white text-[10px] font-bold">✓</span>}
                {!allSelected && selected.size > 0 && <span className="text-orange-600 text-[10px] font-bold">−</span>}
              </div>
              <span className="text-sm font-medium text-gray-700">
                {allSelected ? 'Deselect all' : 'Select all'}
              </span>
              <span className="ml-auto text-xs text-gray-400">{selected.size} of {lines.length} selected</span>
            </div>

            {/* Scrollable lines */}
            <div className="overflow-y-auto flex-1">
              {lines.map((line, i) => (
                <div
                  key={i}
                  onClick={() => toggle(i)}
                  className={`flex items-center gap-3 px-5 py-3 border-b border-gray-50 cursor-pointer transition-colors ${
                    selected.has(i) ? 'bg-orange-50/40 hover:bg-orange-50/60' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                    selected.has(i) ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                  }`}>
                    {selected.has(i) && <span className="text-white text-[10px] font-bold">✓</span>}
                  </div>
                  <span className="font-mono text-sm text-gray-700 w-28 flex-shrink-0 truncate">
                    {line.product_code || <span className="text-gray-400 italic">No code</span>}
                  </span>
                  <span className="flex-1 text-sm text-gray-600 truncate">{line.description || '—'}</span>
                  <span className="text-sm font-semibold text-gray-700 w-16 text-right flex-shrink-0">
                    Qty: {line.qty ?? '—'}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50">
              <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors">
                Cancel
              </button>
              <button
                disabled={selected.size === 0}
                onClick={() => {
                  const toAdd = lines.filter((_, i) => selected.has(i));
                  onAdd(email, toAdd);
                  onClose();
                }}
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: selected.size > 0 ? enconOrange : '#ccc' }}
              >
                <Plus className="w-4 h-4" />
                Add {selected.size} Line{selected.size !== 1 ? 's' : ''}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Recent Emails Strip ─────────────────────────────────────────────────────
function RecentEmailsStrip({ onOpenEmail, addedEmailIds }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Orders only (Sales Acknowledgements) — quotes are not shown in this strip. The feed is mixed, so ask for more than 8 and
        // keep the 8 newest orders; if the larger request is refused, fall back to the plain 8 and filter those.
        let list = [];
        try {
          const wide = await api.get(`${API}/automations/emails?limit=40`);
          list = wide.data?.emails || [];
        } catch {
          const narrow = await api.get(`${API}/automations/emails?limit=8`);
          list = narrow.data?.emails || [];
        }
        const orders = list.filter((e) => e.email_type === 'order').slice(0, 8);
        if (!cancelled) setEmails(orders);
      } catch {
        // Automation not configured yet
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || emails.length === 0) return null;

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const typeStyle = (t) => t === 'order'
    ? { bg: 'bg-orange-100', text: 'text-orange-700', label: 'SA' }
    : t === 'quote'
    ? { bg: 'bg-purple-100', text: 'text-purple-700', label: 'QT' }
    : { bg: 'bg-gray-100', text: 'text-gray-600', label: '?' };

  return (
    <div className="border-b" style={{ borderBottomColor: '#f0f0f0', background: 'linear-gradient(to right, #faf5ff, #f5f3ff)' }}>
      <div className="px-5 py-2 flex items-center gap-2">
        <Mail className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
        <span className="text-xs font-semibold text-purple-700">Imported Customers</span>
        <span className="text-xs text-purple-400">{emails.length} recent</span>
        <button onClick={() => setCollapsed(c => !c)} className="ml-auto text-gray-400 hover:text-gray-600">
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        </button>
      </div>
      {!collapsed && (
        <div className="px-4 pb-3 flex gap-2.5 overflow-x-auto">
          {emails.map(email => {
            const ts = typeStyle(email.email_type);
            const isAdded = addedEmailIds.has(email.id);
            const hasLines = (email.product_lines || []).length > 0;
            return (
              <div
                key={email.id}
                onClick={() => onOpenEmail(email)}
                className={`flex-shrink-0 rounded-xl border bg-white px-3 py-2.5 min-w-[175px] max-w-[210px] shadow-sm cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 ${
                  isAdded ? 'border-green-300 ring-1 ring-green-200' : 'border-purple-100 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ts.bg} ${ts.text}`}>{ts.label}</span>
                  <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5" />{timeAgo(email.received_at)}
                  </span>
                  {isAdded && (
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-auto flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs font-semibold text-gray-800 truncate">
                  {email.customer_name || email.from_email || 'Unknown'}
                </p>
                {email.order_number && (
                  <p className="text-[11px] text-gray-500 font-mono truncate">{email.order_number}</p>
                )}
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-gray-400 truncate">{email.filename}</span>
                  {hasLines && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ml-1 ${
                      isAdded ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {isAdded ? '✓' : email.product_lines.length}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export function StockRequestModal({ isOpen, onClose, initialLines = [], resumeSnapshot = null }) {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const activityIdRef = useRef(resumeSnapshot?.activityId || null);
  const [lines, setLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const v3w = useV3Window(isOpen, 1360, { single: true });

  // Init lines (from a resumed Activity Bar snapshot, PDF pre-population, or empty)
  useEffect(() => {
    if (isOpen) {
      if (resumeSnapshot?.lines?.length > 0) {
        setLines(resumeSnapshot.lines);
      } else if (initialLines && initialLines.length > 0) {
        setLines(initialLines.map(l => ({
          id: Math.random().toString(36).slice(2),
          product_code: l.product_code || '',
          description: l.description || '',
          qty: l.qty != null ? String(l.qty) : '',
          cost_price: l.cost_price || 'List',
        })));
      } else {
        setLines([{ id: Math.random().toString(36).slice(2), product_code: '', description: '', qty: '', cost_price: 'List', notes: '' }]);
      }
    }
  }, [isOpen]);

  // Register the live Activity Bar entry as soon as the modal opens — only
  // "In Progress" for now; it moves to "Recents" once actually submitted.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || activityIdRef.current) return;
    activityIdRef.current = `stock-request-${Date.now()}`;
    activityBar.openActivity({ type: 'stock_request', activityId: activityIdRef.current, title: 'Stock Request', subtitle: 'Request Products', snapshot: { activityId: activityIdRef.current, lines } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2]);

  // Keep the live snapshot fresh as lines are added/edited.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !activityIdRef.current) return;
    const namedLines = lines.filter(l => l.product_code || l.description);
    const title = namedLines.length ? `Stock Request — ${namedLines.length} line${namedLines.length !== 1 ? 's' : ''}` : 'Stock Request';
    activityBar.updateActiveSnapshot({ activityId: activityIdRef.current, lines }, { title, subtitle: 'Request Products' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, lines]);

  // Modal UI v2 keeps the header interactive above the overlay — lock page scroll.
  useEffect(() => {
    if (!isOpen || !modalUiV2) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen, modalUiV2]);

  // Merge lines coming from PDF upload (called while modal stays open)
  const handlePdfConfirm = (pdfLines) => {
    setShowPdfModal(false);
    setLines(prev => {
      const existingCodes = new Set(prev.filter(l => l.product_code).map(l => l.product_code));
      const mapped = pdfLines
        .filter(l => !existingCodes.has(l.product_code))
        .map(l => ({
          id: Math.random().toString(36).slice(2),
          product_code: l.product_code || '',
          description: l.description || '',
          qty: l.qty != null ? String(l.qty) : '',
          cost_price: l.cost_price || 'List',
        }));
      const nonEmpty = prev.filter(l => l.product_code || l.description);
      return [...nonEmpty, ...mapped];
    });
  };

  const addLine = () => {
    setLines(prev => [...prev, { id: Math.random().toString(36).slice(2), product_code: '', description: '', qty: '', cost_price: 'List', notes: '' }]);
  };

  const removeLine = (id) => {
    setLines(prev => prev.filter(l => l.id !== id));
  };

  const updateLine = (id, field, value) => {
    setLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const handleProductSelect = (id, product) => {
    setLines(prev => prev.map(l => l.id === id ? {
      ...l,
      product_code: product.product_code || product.code || '',
      description: product.description || product.name || '',
    } : l));
  };

  const [openEmail, setOpenEmail] = useState(null);
  const [addedEmailIds, setAddedEmailIds] = useState(new Set());

  const handleAddEmailLines = (email, selectedLines) => {
    const newLines = selectedLines.map(l => ({
      id: Math.random().toString(36).slice(2),
      product_code: l.product_code || '',
      description: l.description || '',
      qty: l.qty != null ? String(l.qty) : '1',
      cost_price: 'List',
    }));
    setLines(prev => {
      const nonEmpty = prev.filter(l => l.product_code || l.description);
      return [...newLines, ...nonEmpty];
    });
    setAddedEmailIds(prev => new Set([...prev, email.id]));
    toast.success(`Added ${newLines.length} line${newLines.length !== 1 ? 's' : ''} from ${email.customer_name || 'email'}`);
  };

  const handleUseEmail = (email) => {
    const newLines = (email.product_lines || []).map(l => ({
      id: Math.random().toString(36).slice(2),
      product_code: l.product_code || '',
      description: l.description || '',
      qty: l.qty != null ? String(l.qty) : '1',
      cost_price: 'List',
    }));
    if (newLines.length === 0) {
      toast.info('No product lines found in this email');
      return;
    }
    setLines(prev => {
      const nonEmpty = prev.filter(l => l.product_code || l.description);
      return [...newLines, ...nonEmpty];
    });
    toast.success(`Added ${newLines.length} line${newLines.length !== 1 ? 's' : ''} from ${email.customer_name || 'email'}`);
  };

  const handleSubmit = async () => {    const validLines = lines.filter(l => l.product_code || l.description);
    if (validLines.length === 0) {
      toast.error('Please add at least one product');
      return;
    }
    for (const l of validLines) {
      if (!l.qty || isNaN(Number(l.qty)) || Number(l.qty) <= 0) {
        toast.error(`Please enter a valid quantity for ${l.product_code || 'a product'}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        lines: validLines.map(l => ({
          product_code: l.product_code,
          description: l.description,
          qty: Number(l.qty),
          cost_price: l.cost_price || 'List',
          notes: l.notes || '',
        })),
      };
      await api.post(`${API}/stock-ordering/requests`, payload);
      toast.success(`Stock request submitted — ${validLines.length} product${validLines.length !== 1 ? 's' : ''}`);
      window.dispatchEvent(new Event('c30:stock-changed'));
      // Completed — moves from "In Progress" into "Recents".
      if (modalUiV2 && activityIdRef.current) {
        activityBar.logRecentView({ type: 'stock_request', activityId: activityIdRef.current, title: `Stock Request — ${validLines.length} line${validLines.length !== 1 ? 's' : ''}`, subtitle: 'Completed', snapshot: { activityId: activityIdRef.current, lines: validLines } });
        activityBar.discardActive();
        activityIdRef.current = null;
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-center justify-center ${modalV2OverlayClass(modalUiV2)} ${v3w.className}`}
      {...v3w.attrs}
      style={{ ...(modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 200 }), ...v3w.style }}
    >
      <div
        data-stock-request-modal
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{
          width: 'min(98vw, 1340px)',
          maxHeight: modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 32px)` : '92vh',
          border: `2px solid ${enconOrange}`,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Customer Lines sub-modal */}
        {openEmail && (
          <CustomerLinesModal
            email={openEmail}
            alreadyAdded={addedEmailIds.has(openEmail.id)}
            onAdd={handleAddEmailLines}
            onClose={() => setOpenEmail(null)}
          />
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderBottomColor: '#f0f0f0' }}>
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" style={{ color: enconOrange }} />
            <span className="font-bold text-gray-800 text-lg">Request Products</span>
            <span className="text-xs px-2 py-0.5 rounded-full text-white font-semibold"
              style={{ backgroundColor: enconOrange }}>{lines.length} line{lines.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPdfModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all hover:shadow-sm"
              style={{ borderColor: enconOrange, color: enconOrange }}
              data-testid="stock-request-upload-pdf-btn"
              title="Upload a Sales Acknowledgement PDF to auto-fill lines"
            >
              <Upload className="w-4 h-4" />
              Upload PDF
            </button>
            <button
              onClick={() => { if (modalUiV2) { activityBar.minimizeActive(); activityIdRef.current = null; } onClose(); }}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
              data-testid="stock-request-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Recent Emails Strip */}
        <RecentEmailsStrip onOpenEmail={setOpenEmail} addedEmailIds={addedEmailIds} />

        {/* Column headers */}
        <div className="px-6 pt-3 grid text-xs font-semibold text-gray-700 uppercase tracking-wide"
          style={{ gridTemplateColumns: '180px 1fr 80px 110px 32px', gap: '10px' }}>
          <div>Product Code</div>
          <div>Description</div>
          <div className="text-right">Qty</div>
          <div>Cost Price</div>
          <div></div>
        </div>

        {/* Lines */}
        <div className="flex-1 overflow-y-auto px-6 py-2 space-y-2">
          {lines.map((line, idx) => (
            <div
              key={line.id}
              className="rounded-xl border border-gray-100 hover:border-orange-100 bg-gray-50/40 hover:bg-orange-50/20 transition-colors"
              data-testid={`stock-request-line-${idx}`}
            >
              <div
                className="grid items-center gap-2.5 p-2.5"
                style={{ gridTemplateColumns: '180px 1fr 80px 110px 32px' }}
              >
                {/* Product Code / Search */}
                <ProductSearchDropdown
                  value={line.product_code}
                  onChange={(v) => updateLine(line.id, 'product_code', v)}
                  onSelect={(p) => handleProductSelect(line.id, p)}
                  placeholder="Code…"
                />

                {/* Description — also searchable */}
                <ProductSearchDropdown
                  value={line.description}
                  onChange={(v) => updateLine(line.id, 'description', v)}
                  onSelect={(p) => handleProductSelect(line.id, p)}
                  placeholder="Description…"
                />

                {/* Qty */}
                <Input
                  type="number"
                  min="1"
                  value={line.qty}
                  onChange={e => updateLine(line.id, 'qty', e.target.value)}
                  placeholder="Qty"
                  className="text-sm text-right text-gray-900"
                  data-testid={`stock-request-qty-${idx}`}
                />

                {/* Cost Price */}
                <Input
                  value={line.cost_price}
                  onChange={e => updateLine(line.id, 'cost_price', e.target.value)}
                  placeholder="List"
                  className="text-sm text-gray-900"
                  data-testid={`stock-request-cost-${idx}`}
                />

                {/* Remove */}
                <button
                  onClick={() => removeLine(line.id)}
                  disabled={lines.length === 1}
                  className="text-gray-300 hover:text-red-500 disabled:opacity-20 transition-colors"
                  data-testid={`stock-request-remove-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Per-line notes */}
              <div className="px-2.5 pb-2.5">
                <Input
                  value={line.notes || ''}
                  onChange={e => updateLine(line.id, 'notes', e.target.value)}
                  placeholder="Notes for this line (optional)…"
                  className="text-xs text-gray-900 font-medium border-2 border-orange-200 bg-white focus:border-orange-400 focus:ring-orange-200 h-8 placeholder:text-gray-400"
                  data-testid={`stock-request-notes-${idx}`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between gap-3 bg-gray-50/60">
          <button
            onClick={addLine}
            className="flex items-center gap-1.5 text-sm font-semibold transition-colors"
            style={{ color: enconOrange }}
            data-testid="stock-request-add-line"
          >
            <Plus className="w-4 h-4" /> Add Line
          </button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { if (modalUiV2) { activityBar.minimizeActive(); activityIdRef.current = null; } onClose(); }} disabled={submitting} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="text-white rounded-full"
              style={{ backgroundColor: enconOrange }}
              data-testid="stock-request-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShoppingCart className="w-4 h-4 mr-1" />}
              Send Request
            </Button>
          </div>
        </div>
      </div>
    </div>
    <StockRequestPdfModal
      isOpen={showPdfModal}
      onClose={() => setShowPdfModal(false)}
      onConfirm={handlePdfConfirm}
    />
    </>
  );
}
export function StockRequestPdfModal({ isOpen, onClose, onConfirm }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsedItems, setParsedItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [step, setStep] = useState('upload'); // 'upload' | 'review'
  const fileRef = useRef(null);
  const v3w = useV3Window(isOpen, 640, { single: true });

  useEffect(() => {
    if (isOpen) { setFile(null); setParsedItems([]); setSelected(new Set()); setStep('upload'); }
  }, [isOpen]);

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) { toast.error('Please upload a PDF file'); return; }
    setFile(f);
    setParsing(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await api.post(`${API}/stock-ordering/parse-pdf`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const items = res.data?.items || [];
      if (items.length === 0) {
        toast.warning('No product lines found in this PDF. You can still add items manually.');
        onClose();
        onConfirm([]);
        return;
      }
      setParsedItems(items);
      setSelected(new Set(items.map((_, i) => i)));
      setStep('review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse PDF');
    } finally {
      setParsing(false);
    }
  };

  const toggleAll = () => {
    if (selected.size === parsedItems.length) setSelected(new Set());
    else setSelected(new Set(parsedItems.map((_, i) => i)));
  };

  const toggleItem = (i) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i); else next.add(i);
    setSelected(next);
  };

  const handleConfirm = () => {
    const chosenLines = parsedItems.filter((_, i) => selected.has(i));
    if (chosenLines.length === 0) { toast.error('Please select at least one item'); return; }
    onConfirm(chosenLines);
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[200] flex items-center justify-center bg-black/60 ${v3w.className}`} style={v3w.style} {...v3w.attrs}>
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: 'min(96vw, 640px)', maxHeight: '88vh', border: `2px solid ${enconOrange}` }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5" style={{ color: enconOrange }} />
            <span className="font-bold text-gray-800 text-lg">
              {step === 'upload' ? 'Upload Sales Order PDF' : 'Select Products to Request'}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'upload' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-10">
            <div
              className="w-full max-w-sm border-2 border-dashed rounded-xl p-10 text-center cursor-pointer hover:border-orange-400 transition-colors"
              style={{ borderColor: '#fed7aa' }}
              onClick={() => fileRef.current?.click()}
            >
              {parsing ? (
                <div className="flex flex-col items-center gap-3 text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: enconOrange }} />
                  <p className="font-medium">Parsing PDF…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Upload className="w-8 h-8 text-gray-300" />
                  <p className="font-semibold text-gray-600">Click to upload M4 Sales Order PDF</p>
                  <p className="text-xs text-gray-400">PDF format</p>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept=".pdf" className="hidden"
              onChange={e => handleFile(e.target.files?.[0])} />
          </div>
        )}

        {step === 'review' && (
          <>
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 cursor-pointer">
                <input type="checkbox" checked={selected.size === parsedItems.length} onChange={toggleAll}
                  className="w-4 h-4 accent-orange-500" />
                Select all ({parsedItems.length} items)
              </label>
              <span className="text-sm text-gray-400">{selected.size} selected</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {parsedItems.map((item, i) => (
                <label
                  key={i}
                  className={`flex items-center gap-3 px-5 py-3 border-b cursor-pointer hover:bg-orange-50/50 transition-colors ${selected.has(i) ? 'bg-orange-50/30' : ''}`}
                  data-testid={`pdf-item-${i}`}
                >
                  <input type="checkbox" checked={selected.has(i)} onChange={() => toggleItem(i)}
                    className="w-4 h-4 flex-shrink-0 accent-orange-500" />
                  <span className="font-mono text-sm font-bold text-gray-800 w-28 flex-shrink-0">{item.product_code}</span>
                  <span className="flex-1 text-sm text-gray-600 truncate">{item.description}</span>
                  <span className="text-sm font-semibold text-gray-700 flex-shrink-0">Qty: {item.qty}</span>
                </label>
              ))}
            </div>
            <div className="px-5 py-4 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleConfirm} className="text-white" style={{ backgroundColor: enconOrange }}
                data-testid="pdf-confirm-btn">
                Add {selected.size} Item{selected.size !== 1 ? 's' : ''} to Request
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default StockRequestModal;

// ─── Stock Request Detail Modal ──────────────────────────────────────────────
export function StockRequestDetailModal({ userEmail, requests, onClose, onUpdate, branchId }) {
  const [lineStates, setLineStates] = useState({});
  const [saving, setSaving] = useState({});
  const [showCompleted, setShowCompleted] = useState(false);
  const [noteOpen, setNoteOpen] = useState({});     // key → bool: is note input open
  const [noteDraft, setNoteDraft] = useState({});   // key → string: draft text
  const [noteSaving, setNoteSaving] = useState({}); // key → bool

  const userName = requests[0]?.user_name || userEmail;
  const v3w = useV3Window(!!userEmail, 860, { single: true });

  useEffect(() => {
    const init = {};
    for (const req of requests) {
      for (let i = 0; i < (req.lines || []).length; i++) {
        init[`${req.id}-${i}`] = !!req.lines[i].purchased;
      }
    }
    setLineStates(init);
  }, [requests]);

  const handleToggle = async (reqId, lineIdx, currentVal) => {
    const key = `${reqId}-${lineIdx}`;
    const newVal = !currentVal;
    setLineStates(prev => ({ ...prev, [key]: newVal }));
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await api.patch(
        `${API}/stock-ordering/requests/${reqId}/lines`,
        { lines: [{ idx: lineIdx, purchased: newVal }] },
        { params: { branch_id: branchId } }
      );
      if (onUpdate) onUpdate({ id: reqId, lines: res.data.lines });
    } catch {
      setLineStates(prev => ({ ...prev, [key]: currentVal }));
      toast.error('Failed to update purchase status');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleNotOrdered = async (reqId, lineIdx) => {
    const key = `${reqId}-${lineIdx}`;
    setSaving(prev => ({ ...prev, [key]: true }));
    setLineStates(prev => ({ ...prev, [key]: true })); // move to completed visually
    try {
      const res = await api.patch(
        `${API}/stock-ordering/requests/${reqId}/lines`,
        { lines: [{ idx: lineIdx, not_ordered: true }] },
        { params: { branch_id: branchId } }
      );
      if (onUpdate) onUpdate({ id: reqId, lines: res.data.lines });
      toast.success('Marked as not ordered');
    } catch {
      setLineStates(prev => ({ ...prev, [key]: false }));
      toast.error('Failed to mark as not ordered');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveNote = async (reqId, lineIdx, noteText) => {
    const key = `${reqId}-${lineIdx}`;
    setNoteSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await api.patch(
        `${API}/stock-ordering/requests/${reqId}/lines`,
        { lines: [{ idx: lineIdx, note: noteText }] },
        { params: { branch_id: branchId } }
      );
      if (onUpdate) onUpdate({ id: reqId, lines: res.data.lines });
      setNoteOpen(prev => ({ ...prev, [key]: false }));
      toast.success(noteText ? 'Note saved' : 'Note cleared');
    } catch {
      toast.error('Failed to save note');
    } finally {
      setNoteSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  if (!userEmail) return null;

  const allLines = requests.flatMap(req =>
    (req.lines || []).map((line, i) => ({ ...line, reqId: req.id, lineIdx: i, reqDate: req.created_at }))
  );
  const toOrderLines = allLines.filter(line => !lineStates[`${line.reqId}-${line.lineIdx}`]);
  const completedLines = allLines.filter(line => !!lineStates[`${line.reqId}-${line.lineIdx}`]);
  const displayLines = showCompleted ? completedLines : toOrderLines;

  return (
    <div className={`fixed inset-0 z-[210] flex items-center justify-center bg-black/60 ${v3w.className}`} style={v3w.style} {...v3w.attrs}>
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: 'min(98vw, 860px)', maxHeight: '88vh', border: `2px solid ${enconOrange}` }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderBottomColor: '#f0f0f0' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <ShoppingCart className="w-5 h-5" style={{ color: enconOrange }} />
            <span className="font-bold text-gray-800 text-lg">{userName}</span>
            <span className="text-xs px-2 py-0.5 rounded-full text-white font-semibold"
              style={{ backgroundColor: enconOrange }}>
              {toOrderLines.length} to order
            </span>
            {completedLines.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full text-white font-semibold bg-green-500">
                {completedLines.length} completed
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCompleted(s => !s)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition-all ${showCompleted ? 'bg-green-500 text-white border-green-500' : 'text-gray-600 border-gray-300 hover:border-orange-300'}`}
              data-testid="show-completed-toggle"
            >
              {showCompleted ? 'Show To Order' : 'Show Completed'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Lines table */}
        <div className="flex-1 overflow-y-auto">
          {displayLines.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">
              {showCompleted ? 'No completed items yet' : 'All items have been completed'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-500 uppercase tracking-wide font-semibold w-10">Done</th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-500 uppercase tracking-wide font-semibold">Code</th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-500 uppercase tracking-wide font-semibold">Description</th>
                  <th className="px-4 py-2.5 text-right text-xs text-gray-500 uppercase tracking-wide font-semibold">Qty</th>
                  <th className="px-4 py-2.5 text-right text-xs text-gray-500 uppercase tracking-wide font-semibold">Cost</th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-500 uppercase tracking-wide font-semibold">Requested</th>
                  <th className="px-4 py-2.5 text-center text-xs text-gray-500 uppercase tracking-wide font-semibold w-10">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayLines.map((line) => {
                  const key = `${line.reqId}-${line.lineIdx}`;
                  const isPurchased = !!lineStates[key];
                  const isNotOrdered = !!line.not_ordered;
                  const hasNote = !!line.note;
                  const hasRequesterNote = !!line.notes;
                  const isNoteOpen = !!noteOpen[key];
                  const draft = noteDraft[key] ?? (line.note || '');
                  const rowBg = isPurchased
                    ? (isNotOrdered ? 'bg-red-50' : 'bg-green-50')
                    : 'hover:bg-gray-50';
                  return (
                    <>
                    <tr key={key}
                      className={`transition-colors ${rowBg}`}
                      data-testid={`req-detail-line-${key}`}
                    >
                      <td className="px-2 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <input type="checkbox" checked={isPurchased && !isNotOrdered}
                            onChange={() => handleToggle(line.reqId, line.lineIdx, isPurchased)}
                            disabled={!!saving[key]}
                            className="w-4 h-4 accent-green-500 cursor-pointer"
                            data-testid={`req-purchase-cb-${key}`}
                          />
                          {!isPurchased && (
                            <button
                              onClick={() => handleNotOrdered(line.reqId, line.lineIdx)}
                              disabled={!!saving[key]}
                              title="Mark as not ordered"
                              className="p-0.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              data-testid={`req-not-ordered-btn-${key}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 py-3 font-mono font-bold ${isPurchased ? (isNotOrdered ? 'text-red-400 line-through' : 'text-gray-400 line-through') : 'text-gray-800'}`}>
                        {line.product_code || '—'}
                      </td>
                      <td className={`px-4 py-3 max-w-xs truncate ${isPurchased ? (isNotOrdered ? 'text-red-400 line-through' : 'text-gray-400 line-through') : 'text-gray-600'}`}>
                        {line.description || '—'}
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums font-semibold ${isPurchased ? (isNotOrdered ? 'text-red-400' : 'text-gray-400') : 'text-gray-700'}`}>
                        {line.qty}
                      </td>
                      <td className={`px-4 py-3 text-right ${isPurchased ? (isNotOrdered ? 'text-red-400' : 'text-gray-400') : 'text-gray-500'}`}>
                        {line.cost_price || 'List'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {isNotOrdered && <span className="text-[10px] font-bold text-red-500 mr-1">NOT ORDERED</span>}
                        {line.reqDate ? new Date(line.reqDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => {
                            setNoteOpen(prev => ({ ...prev, [key]: !isNoteOpen }));
                            setNoteDraft(prev => ({ ...prev, [key]: line.note || '' }));
                          }}
                          title={hasNote ? line.note : 'Add note'}
                          className={`p-1 rounded transition-colors ${hasNote ? 'text-orange-500 hover:bg-orange-50' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'}`}
                          data-testid={`req-note-btn-${key}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    {/* Requester's original note (read-only) */}
                    {hasRequesterNote && (
                      <tr key={`${key}-req-note`} className="bg-blue-50/40">
                        <td colSpan={7} className="px-4 py-1.5">
                          <div className="flex items-center gap-1.5 text-xs text-blue-700">
                            <MessageSquare className="w-3 h-3 text-blue-400 flex-shrink-0" />
                            <span className="font-semibold text-blue-500 mr-0.5">Requester note:</span>
                            <span>{line.notes}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Inline note editor row */}
                    {isNoteOpen && (
                      <tr key={`${key}-note`} className="bg-orange-50 border-b border-orange-100">
                        <td colSpan={7} className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <PenLine className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                            <input
                              type="text"
                              value={draft}
                              onChange={e => setNoteDraft(prev => ({ ...prev, [key]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') handleSaveNote(line.reqId, line.lineIdx, draft); if (e.key === 'Escape') setNoteOpen(prev => ({ ...prev, [key]: false })); }}
                              placeholder="Add a note for this item…"
                              autoFocus
                              className="flex-1 text-xs border border-orange-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                              data-testid={`req-note-input-${key}`}
                            />
                            <button
                              onClick={() => handleSaveNote(line.reqId, line.lineIdx, draft)}
                              disabled={!!noteSaving[key]}
                              className="text-xs px-3 py-1.5 rounded-lg text-white font-semibold transition-opacity hover:opacity-80"
                              style={{ backgroundColor: enconOrange }}
                              data-testid={`req-note-save-${key}`}
                            >
                              {noteSaving[key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                            </button>
                            <button
                              onClick={() => setNoteOpen(prev => ({ ...prev, [key]: false }))}
                              className="text-xs px-2 py-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                          {line.note_by && (
                            <p className="text-[10px] text-gray-400 mt-1 pl-5">
                              Last note by {line.note_by}{line.note_at ? ` · ${new Date(line.note_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                    </>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between text-sm text-gray-400">
          <span>{toOrderLines.length} remaining · {completedLines.length} completed</span>
          <Button onClick={onClose} className="text-white rounded-full" style={{ backgroundColor: enconOrange }}>Done</Button>
        </div>
      </div>
    </div>
  );
}

// One completed line in the Request History table, plus — directly underneath it — the notes that go with it:
// the note the requester put on the line when they asked for it, and the note the team added when they completed it
// (with who wrote it and when). Notes used to be returned by the server but never shown here.
export function HistoryLineRows({ line }) {
  const requesterNote = String(line.notes || '').trim();
  const completionNote = String(line.note || '').trim();
  const noteDate = line.note_at
    ? new Date(line.note_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '';
  return (
    <Fragment>
      <tr className={line.not_ordered ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}>
        <td className={`px-4 py-2.5 font-mono font-bold text-xs ${line.not_ordered ? 'text-red-500' : 'text-gray-800'}`}>{line.product_code}</td>
        <td className={`px-4 py-2.5 text-xs truncate max-w-xs ${line.not_ordered ? 'text-red-400 line-through' : 'text-gray-600'}`}>{line.description}</td>
        <td className={`px-4 py-2.5 text-right font-semibold ${line.not_ordered ? 'text-red-400' : 'text-gray-700'}`}>{line.qty}</td>
        <td className="px-4 py-2.5 text-xs text-gray-400">
          {line.not_ordered
            ? <span className="text-[10px] font-bold text-red-500 bg-red-100 px-1.5 py-0.5 rounded-full">NOT ORDERED</span>
            : (line.purchased_at ? new Date(line.purchased_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—')
          }
        </td>
      </tr>
      {requesterNote && (
        <tr className="bg-blue-50/50" data-testid="history-requester-note">
          <td colSpan={4} className="px-4 py-1.5">
            <div className="flex items-start gap-1.5 text-xs text-blue-700">
              <MessageSquare className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
              <span className="font-semibold text-blue-500 mr-0.5 flex-shrink-0">Requester note:</span>
              <span className="break-words min-w-0">{requesterNote}</span>
            </div>
          </td>
        </tr>
      )}
      {completionNote && (
        <tr className="bg-orange-50/70" data-testid="history-completion-note">
          <td colSpan={4} className="px-4 py-1.5">
            <div className="flex items-start gap-1.5 text-xs text-orange-800">
              <PenLine className="w-3 h-3 text-orange-400 flex-shrink-0 mt-0.5" />
              <span className="font-semibold text-orange-600 mr-0.5 flex-shrink-0">Note:</span>
              <span className="break-words min-w-0">{completionNote}</span>
            </div>
            {(line.note_by || noteDate) && (
              <p className="text-[10px] text-gray-400 mt-0.5 pl-[18px]">
                {line.note_by ? `by ${line.note_by}` : ''}{line.note_by && noteDate ? ' · ' : ''}{noteDate}
              </p>
            )}
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// ─── Stock History Modal ──────────────────────────────────────────────────────
export function StockHistoryModal({ branchId, onClose }) {
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [fullYear, setFullYear] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [yearDropOpen, setYearDropOpen] = useState(false);
  const v3w = useV3Window(true, 900, { single: true });

  const years = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/stock-ordering/requests/history`, {
        params: { branch_id: branchId, year, month: fullYear ? undefined : month, full_year: fullYear }
      });
      setData(res.data.history || []);
    } catch { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  }, [branchId, year, month, fullYear]);

  useEffect(() => { load(); }, [load]);

  const q = search.toLowerCase().trim();
  const filteredData = data.map(u => {
    if (!q) return u;
    const nameMatch = u.user_name.toLowerCase().includes(q);
    const filteredLines = u.lines.filter(l =>
      (l.product_code || '').toLowerCase().includes(q) ||
      (l.description || '').toLowerCase().includes(q) ||
      (l.notes || '').toLowerCase().includes(q) ||
      (l.note || '').toLowerCase().includes(q)
    );
    if (!nameMatch && filteredLines.length === 0) return null;
    return { ...u, lines: nameMatch ? u.lines : filteredLines };
  }).filter(Boolean);

  return (
    <div className={`fixed inset-0 z-[210] flex items-center justify-center bg-black/60 ${v3w.className}`} style={v3w.style} {...v3w.attrs} onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: 'min(98vw, 900px)', maxHeight: '90vh', border: `2px solid ${enconOrange}` }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5" style={{ color: enconOrange }} />
            <span className="font-bold text-gray-800 text-lg">Request History</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b bg-gray-50">
          {/* Year picker */}
          <div className="relative">
            <button
              onClick={() => setYearDropOpen(o => !o)}
              className="flex items-center gap-1 text-sm font-semibold px-3 py-1.5 border rounded-lg hover:bg-white transition-colors"
              data-testid="history-year-btn"
            >
              {year} <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {yearDropOpen && (
              <div className="absolute top-full mt-1 left-0 bg-white border rounded-xl shadow-lg z-10 py-1 w-24">
                {years.map(y => (
                  <button key={y} onClick={() => { setYear(y); setYearDropOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-orange-50 ${y === year ? 'font-bold text-orange-600' : 'text-gray-700'}`}>
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Full year toggle */}
          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={fullYear} onChange={e => setFullYear(e.target.checked)}
              className="w-4 h-4 accent-orange-500" />
            Full year
          </label>

          {/* Month pills — hidden when full year */}
          {!fullYear && (
            <div className="flex flex-wrap gap-1">
              {MONTHS.map((m, i) => (
                <button key={m} onClick={() => setMonth(i + 1)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${month === i + 1 ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  style={month === i + 1 ? { backgroundColor: enconOrange } : {}}>
                  {m}
                </button>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="relative ml-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search product, note or user…"
              className="pl-8 pr-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 w-56"
              style={{ '--tw-ring-color': enconOrange + '60' }}
              data-testid="history-search"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading ? (
            <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
          ) : filteredData.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-sm">
              No completed requests found for {fullYear ? year : `${MONTHS[month - 1]} ${year}`}
            </div>
          ) : filteredData.map(user => (
            <div key={user.user_email} className="border rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                onClick={() => setExpandedUser(e => e === user.user_email ? null : user.user_email)}
                data-testid={`history-user-${user.user_email}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                    style={{ backgroundColor: enconOrange }}>
                    {(user.user_name || user.user_email)[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">{user.user_name}</div>
                    <div className="text-xs text-gray-400">{user.user_email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2 py-0.5 rounded-full text-white font-semibold bg-green-500">
                    {user.total_lines} item{user.total_lines !== 1 ? 's' : ''}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedUser === user.user_email ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {expandedUser === user.user_email && (
                <div className="border-t">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50/60">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wide">Code</th>
                        <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wide">Description</th>
                        <th className="px-4 py-2 text-right text-xs text-gray-500 uppercase tracking-wide">Qty</th>
                        <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase tracking-wide">Completed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {user.lines.map((line, i) => (
                        <HistoryLineRows key={`${line.req_id || 'r'}-${i}`} line={line} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="px-6 py-3 border-t bg-gray-50 text-right">
          <Button onClick={onClose} className="text-white rounded-full" style={{ backgroundColor: enconOrange }}>Close</Button>
        </div>
      </div>
    </div>
  );
}
